import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMyDailyDirectClient } from "@mydailydirect/app-kit";
import {
  DESIGN_DIRECTIONS,
  applyDesignDirection,
  readDesignDirection,
  type DesignDirection,
  Button,
  Badge,
} from "@mydailydirect/ui";
import {
  Smartphone,
  Monitor,
  Tablet,
  ShoppingCart,
  Palette,
} from "lucide-react";
import { BrowsePage } from "./BrowsePage";
import { CartDrawer } from "../components/CartDrawer";
import { useCart } from "../lib/cart";

type Viewport = "desktop" | "mobile" | "tablet";

export function DesignLabPage() {
  const client = useMyDailyDirectClient();
  const cart = useCart();

  const [activeDirection, setActiveDirection] = useState<DesignDirection | "legacy">(() => {
    return readDesignDirection() || "mandi";
  });

  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [isCartOpen, setIsCartOpen] = useState(false);

  const { data: liveSkus = [] } = useQuery({
    queryKey: ["skus"],
    queryFn: () => client.skus.list(),
  });

  // Apply selected direction to <html>
  useEffect(() => {
    if (activeDirection === "legacy") {
      applyDesignDirection(null);
    } else {
      applyDesignDirection(activeDirection);
    }
  }, [activeDirection]);

  const selectDirection = (dir: DesignDirection | "legacy") => {
    setActiveDirection(dir);
  };

  const activeMeta = DESIGN_DIRECTIONS.find((d) => d.id === activeDirection);

  const cartTotalPaise = cart.lines.reduce((sum, line) => {
    const sku = liveSkus.find((s) => s.id === line.skuId);
    return sum + (sku ? sku.pricePaise * line.qty : 0);
  }, 0);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col font-sans">
      {/* Top Floating Control Bar */}
      <header className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-slate-800 px-3 sm:px-6 py-2.5 shadow-xl">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          {/* Title & Direction Switcher */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 flex items-center justify-center shrink-0">
                <Palette className="h-4 w-4" />
              </div>
              <div className="leading-tight">
                <div className="text-xs font-bold text-white flex items-center gap-1.5">
                  Design Direction Lab
                  <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 font-mono">
                    Retailer Catalog
                  </span>
                </div>
                <div className="text-[11px] text-slate-400">
                  {activeMeta ? activeMeta.tagline : "Legacy Look (Current baseline)"}
                </div>
              </div>
            </div>

            {/* Direction Pills */}
            <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 flex-wrap">
              {DESIGN_DIRECTIONS.map((dir) => {
                const isSelected = activeDirection === dir.id;
                return (
                  <button
                    key={dir.id}
                    type="button"
                    onClick={() => selectDirection(dir.id)}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
                      isSelected
                        ? "bg-emerald-500 text-slate-950 font-bold shadow-sm"
                        : "text-slate-300 hover:text-white hover:bg-slate-800"
                    }`}
                  >
                    <span className="font-mono text-[10px] opacity-75">{dir.letter}</span>
                    <span>{dir.label}</span>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => selectDirection("legacy")}
                className={`px-2 py-1 text-xs font-medium rounded-lg transition-all cursor-pointer ${
                  activeDirection === "legacy"
                    ? "bg-slate-700 text-white font-bold"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                Baseline
              </button>
            </div>
          </div>

          {/* Viewport Frame and Cart Drawer Actions */}
          <div className="flex items-center gap-2 shrink-0 self-end lg:self-auto">
            {/* Viewport mode switcher */}
            <div className="flex items-center bg-slate-900 p-0.5 rounded-lg border border-slate-800 text-xs">
              <button
                type="button"
                onClick={() => setViewport("desktop")}
                title="Desktop 1440px viewport"
                className={`p-1.5 rounded flex items-center gap-1 cursor-pointer ${
                  viewport === "desktop"
                    ? "bg-slate-800 text-emerald-400 font-bold"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Monitor className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Desktop</span>
              </button>
              <button
                type="button"
                onClick={() => setViewport("tablet")}
                title="Tablet 768px viewport"
                className={`p-1.5 rounded flex items-center gap-1 cursor-pointer ${
                  viewport === "tablet"
                    ? "bg-slate-800 text-emerald-400 font-bold"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Tablet className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Tablet</span>
              </button>
              <button
                type="button"
                onClick={() => setViewport("mobile")}
                title="Mobile 390px (iPhone viewport)"
                className={`p-1.5 rounded flex items-center gap-1 cursor-pointer ${
                  viewport === "mobile"
                    ? "bg-slate-800 text-emerald-400 font-bold"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Smartphone className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Mobile (390px)</span>
              </button>
            </div>

            {/* Test Cart Drawer button */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsCartOpen(true)}
              className="h-8 border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800 text-xs font-bold gap-1.5"
            >
              <ShoppingCart className="h-3.5 w-3.5 text-emerald-400" />
              <span>Preview Cart</span>
              <Badge variant="default" size="sm" className="ml-0.5 bg-emerald-500 text-slate-950 font-bold">
                {cart.totalItems}
              </Badge>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Viewport Workspace */}
      <main className="flex-1 p-2 sm:p-6 flex items-start justify-center overflow-auto bg-slate-950/60">
        <div
          className={`transition-all duration-300 ${
            viewport === "mobile"
              ? "w-[390px] min-h-[844px] bg-background text-foreground rounded-[40px] shadow-2xl border-[10px] border-slate-800 ring-1 ring-slate-700/50 overflow-hidden my-4 relative"
              : viewport === "tablet"
              ? "w-[768px] min-h-[1024px] bg-background text-foreground rounded-2xl shadow-2xl border border-slate-800 overflow-hidden my-4"
              : "w-full max-w-6xl bg-background text-foreground rounded-xl shadow-lg border border-slate-800/60 overflow-hidden"
          }`}
        >
          {/* Simulated Mobile Status Bar if in mobile view */}
          {viewport === "mobile" && (
            <div className="bg-background text-foreground px-6 pt-3 pb-1 flex items-center justify-between text-[11px] font-bold border-b border-border/40 select-none">
              <span>9:41</span>
              <div className="h-3.5 w-20 bg-slate-950/20 dark:bg-slate-100/20 rounded-full" />
              <div className="flex items-center gap-1 text-[10px]">
                <span>5G</span>
                <span>100%</span>
              </div>
            </div>
          )}

          {/* Render the refactored BrowsePage inside the canvas */}
          <div className="p-2 sm:p-4 bg-background min-h-[700px]">
            <BrowsePage />
          </div>
        </div>
      </main>

      {/* Embedded Cart Drawer */}
      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartTotal={cartTotalPaise}
        onPlaceOrder={(method) => {
          alert(`Demo Order Placed via ${method}! Total: ₹${(cartTotalPaise / 100).toFixed(2)}`);
          setIsCartOpen(false);
        }}
        isPlacingOrder={false}
      />
    </div>
  );
}
